import { useEffect, useState, type FormEvent } from 'react';
import {
  MessagesSquare,
  CheckCircle2,
  ShieldCheck,
  Clock,
  TrendingUp,
  Hotel,
  Dices,
  Building2,
  Truck,
  Store,
  Wrench,
  ArrowRight,
  Quote,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Landing pública de "Soporte Vipsoft — Operaciones internas sobre WhatsApp".
 *
 * Posicionamiento B2B operaciones internas (NO atención al cliente final).
 * Copy basado en el pitch interno aprobado (variante A + C).
 *
 * Form de captura: reutilizamos POST /widget/tickets (canal=widget). Cada
 * lead entra al panel como ticket en pipeline 'operaciones-whatsapp-landing'.
 */

const VERTICALS = [
  { icon: Hotel, title: 'Hotelería boutique', body: 'Recepción ↔ housekeeping ↔ mantenimiento ↔ F&B coordinados en un solo grupo.' },
  { icon: Dices, title: 'Casinos y salas', body: 'Reportes de incidencias por turno con foto, ubicación y severidad.' },
  { icon: Building2, title: 'Property management', body: 'Mantenimiento, portería y limpieza en una sola conversación trazada.' },
  { icon: Truck, title: 'Distribuidoras', body: 'Vendedores en ruta pasando pedidos al depósito sin abrir un ERP.' },
  { icon: Store, title: 'Cadenas multi-sucursal', body: 'Encargados y central sin tener que leer 14 grupos de WhatsApp.' },
  { icon: Wrench, title: 'Field service', body: 'Cuadrillas técnicas reportando estado de trabajo con foto y geo opcional.' },
];

interface LeadForm {
  name: string;
  email: string;
  company: string;
  industry: string;
  groups_count: string;
  employees: string;
}

const INITIAL_FORM: LeadForm = {
  name: '',
  email: '',
  company: '',
  industry: '',
  groups_count: '',
  employees: '',
};

export default function OperacionesWhatsapp() {
  const [form, setForm] = useState<LeadForm>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    try {
      const body = {
        name: form.name,
        email: form.email,
        subject: `Lead operaciones WhatsApp — ${form.company || form.name}`,
        message: [
          `Empresa: ${form.company || '(no indicó)'}`,
          `Rubro: ${form.industry || '(no indicó)'}`,
          `Grupos operativos activos: ${form.groups_count || '(no indicó)'}`,
          `Empleados: ${form.employees || '(no indicó)'}`,
          ``,
          `Origen: landing /operaciones-whatsapp`,
        ].join('\n'),
        site: 'operaciones-whatsapp-landing',
        page_url: window.location.href,
      };
      const res = await fetch('/widget/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Error al enviar.');
      }
      setSubmitted(true);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al enviar.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    document.title = 'Operaciones internas sobre WhatsApp — Vipsoft Ops';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      'content',
      'Tu equipo ya coordina la operación por WhatsApp. Hacelo medible sin sacarlos de ahí. Una mención al bot y la tarea queda como ticket con SLA y responsable.',
    );
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">


      {/* === Nav top === */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold tracking-tight">
            <MessagesSquare size={18} className="text-emerald-600" />
            <span>Vipsoft Ops</span>
            <span className="text-xs text-slate-400 font-normal">(beta)</span>
          </div>
          <a
            href="#probar"
            className="text-sm px-3 py-1.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition"
          >
            Quiero probar
          </a>
        </div>
      </header>

      {/* === Hero === */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-16">
        <div className="max-w-3xl">
          <span className="inline-block px-2.5 py-1 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full">
            Para empresas que operan por grupos de WhatsApp
          </span>
          <h1 className="mt-5 text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
            Tu equipo ya coordina la operación por WhatsApp.{' '}
            <span className="text-emerald-700">Hacelo medible sin sacarlos de ahí.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Una mención al bot dentro del grupo del equipo y la tarea queda como ticket: asignada, con SLA, con responsable.
            <br />
            Tu gente sigue usando WhatsApp. Vos por fin ves qué pasa.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="#probar"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition"
            >
              Probar 30 días <ArrowRight size={16} />
            </a>
            <a
              href="#como-funciona"
              className="px-4 py-2.5 text-slate-700 hover:text-slate-900 transition"
            >
              Ver cómo funciona
            </a>
          </div>
          <div className="mt-8 flex items-center gap-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={12} className="text-emerald-600" /> Self-hosted opcional
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={12} className="text-emerald-600" /> Soporte en español, LATAM
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={12} className="text-emerald-600" /> Setup en horas, no semanas
            </span>
          </div>
        </div>
      </section>

      {/* === Problema === */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-3xl font-semibold tracking-tight max-w-2xl">
            &quot;¿Quién quedó con el pedido de la 304?&quot;
          </h2>
          <p className="mt-3 text-lg text-slate-600 max-w-2xl">
            Si esa pregunta aparece a las 11 de la noche en tu negocio, este producto es para ti.
          </p>

          <div className="mt-12 grid md:grid-cols-2 gap-x-12 gap-y-8">
            <Pain
              title="Los grupos de WhatsApp son tu sistema operativo real"
              body="Recepción coordinando con housekeeping. Encargados de turno reportando incidencias. Vendedores pasando pedidos al depósito. Todo vive ahí, mezclado con fotos de cumpleaños y memes."
            />
            <Pain
              title="Nada queda trazado"
              body="No hay SLA, no hay asignación, no hay reporting. Cuando algo sale mal, nadie sabe quién recibió el pedido, cuándo, ni si se resolvió. El dueño no puede medir productividad por turno."
            />
            <Pain
              title="Cuando renuncia un empleado clave, se va con dos años de contexto"
              body="El nuevo arranca de cero. No hay onboarding posible porque no hay historial fuera del celular del que se fue."
            />
            <Pain
              title="Las herramientas serias no se pueden implementar"
              body="ServiceNow y Jira Service Management cuestan USD 50-200 por agente al mes. Slack + Trello obligan a tu equipo a cambiar de app — nadie del staff va a abrir Slack mientras cambia sábanas o atiende caja."
            />
          </div>
        </div>
      </section>

      {/* === Solución === */}
      <section id="como-funciona" className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-semibold tracking-tight">Cómo funciona</h2>
          <p className="mt-3 text-lg text-slate-600">
            Agregás el bot al grupo del equipo. Cuando alguien necesita coordinar algo, lo etiqueta. Listo.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Step
            n={1}
            title="Tu equipo escribe en el grupo, como siempre"
            body="Mismas conversaciones, mismo grupo, misma app. Nadie aprende nada nuevo."
            sample="@VipsoftBot habitación 304 pide toallas adicionales urgente"
          />
          <Step
            n={2}
            title="El bot crea el ticket"
            body="En 5 segundos hay un ticket en el panel con prioridad, foto/audio si se mandó, asignado al área correcta."
            sample="✅ Ticket VIPSOFT-2026-001247 creado. Te respondemos pronto."
          />
          <Step
            n={3}
            title="El responsable resuelve desde el panel"
            body="Confirma resolución → el bot avisa al grupo etiquetando a quien lo abrió. Métricas al panel, no al equipo."
            sample="✔️ Ticket VIPSOFT-2026-001247 resuelto. @recepcion"
          />
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Feature
            icon={ShieldCheck}
            title="Sin vigilancia"
            body="El bot solo procesa menciones explícitas. No lee el grupo, no graba audios que no le pidieron, no trackea ubicación. Es tracking de tareas, no de personas."
          />
          <Feature
            icon={Clock}
            title="SLA configurable"
            body="Cada tipo de tarea con su tiempo objetivo. El panel marca rojo cuando algo se desvía y lo registra para el reporte mensual."
          />
          <Feature
            icon={TrendingUp}
            title="Integración a tu sistema"
            body="Webhooks bidireccionales con tu PMS, ERP, sistema de inventario o lo que tengas. Un pedido de toallas puede descontar stock automáticamente."
          />
        </div>
      </section>

      {/* === Verticales === */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-3xl font-semibold tracking-tight max-w-2xl">
            Lo usan equipos que viven en WhatsApp
          </h2>
          <p className="mt-3 text-lg text-slate-600 max-w-2xl">
            Mismo motor, distinto vertical. Si tu operación pasa por uno o más grupos, vas a verlo.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {VERTICALS.map((v) => (
              <div key={v.title} className="p-5 rounded-xl border border-slate-200 bg-slate-50/50">
                <v.icon size={20} className="text-emerald-700" />
                <h3 className="mt-3 font-semibold text-slate-900">{v.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === Diferenciadores === */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-semibold tracking-tight">Versus lo que ya probaste</h2>
          <p className="mt-3 text-lg text-slate-600">
            El competidor real no es Zendesk ni ServiceNow. Es <strong>no tener nada</strong>,
            que es lo que el 90% de las PYMEs LATAM tienen hoy.
          </p>
        </div>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 pr-4 font-medium text-slate-500"></th>
                <th className="text-left py-3 px-4 font-semibold bg-emerald-50 text-emerald-900 rounded-t-lg">Vipsoft Ops</th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">Status quo</th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">Jira / ServiceNow</th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">Slack + Trello + bots</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row) => (
                <tr key={row.label} className="border-b border-slate-100">
                  <td className="py-3 pr-4 text-slate-700">{row.label}</td>
                  <td className="py-3 px-4 bg-emerald-50/50 text-emerald-900 font-medium">{row.ours}</td>
                  <td className="py-3 px-4 text-slate-500">{row.statusQuo}</td>
                  <td className="py-3 px-4 text-slate-500">{row.enterprise}</td>
                  <td className="py-3 px-4 text-slate-500">{row.slack}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* === Pricing === */}
      <section className="border-t border-slate-200 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-semibold tracking-tight">Precio claro, por empleado activo</h2>
            <p className="mt-3 text-lg text-slate-300">
              No te cobramos por ticket (los tickets son el valor, no el costo). Te cobramos por la gente
              que efectivamente usa el bot en tus grupos.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <PricingCard
              tier="Starter"
              price="$79–129"
              cap="Hasta 10 empleados"
              groups="3 grupos"
              agents="2 agentes"
              integrations="1 integración"
            />
            <PricingCard
              tier="Growth"
              price="$249–399"
              cap="Hasta 50 empleados"
              groups="15 grupos"
              agents="8 agentes"
              integrations="3 integraciones"
              highlight
            />
            <PricingCard
              tier="Scale"
              price="$599–999"
              cap="Empleados ilimitados"
              groups="Grupos ilimitados"
              agents="Agentes ilimitados"
              integrations="Integraciones ilimitadas"
            />
          </div>
          <p className="mt-6 text-xs text-slate-400">
            Precios en USD/mes, sujetos a confirmación tras pilotos. Setup inicial USD 149–699 según tier (incluye conexión del bot, integración a tu sistema actual y capacitación del equipo).
          </p>
        </div>
      </section>

      {/* === Objeción principal === */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-3xl">
          <Quote size={28} className="text-slate-300" />
          <blockquote className="mt-4 text-2xl font-semibold tracking-tight leading-snug text-slate-900">
            &quot;¿Mis empleados se van a sentir vigilados?&quot;
          </blockquote>
          <p className="mt-5 text-slate-700 leading-relaxed">
            Justa pregunta. El bot solo procesa lo que se le menciona explícitamente. No lee conversaciones del grupo, no graba audios no destinados a tickets, no trackea ubicación.
          </p>
          <p className="mt-3 text-slate-700 leading-relaxed">
            Es tracking de <strong>tareas</strong> (como cualquier sistema operativo de empresa), no de personas. De hecho, protege al empleado: si pidió algo y nadie lo atendió, queda registro de que él cumplió su parte. <strong>El que hace su trabajo, gana con trazabilidad.</strong>
          </p>
        </div>
      </section>

      {/* === CTA / Form === */}
      <section id="probar" className="border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-20">
          {submitted ? (
            <div className="text-center py-10">
              <CheckCircle2 size={48} className="mx-auto text-emerald-600" />
              <h2 className="mt-5 text-2xl font-semibold tracking-tight text-slate-900">
                Pedido recibido. Te escribimos en menos de 24h.
              </h2>
              <p className="mt-3 text-slate-600">
                Vamos a coordinar una demo en vivo (15 minutos) con el grupo de WhatsApp de prueba en pantalla.
              </p>
            </div>
          ) : (
            <>
              <div className="max-w-2xl">
                <h2 className="text-3xl font-semibold tracking-tight text-slate-900">Probalo 30 días</h2>
                <p className="mt-3 text-lg text-slate-600">
                  Setup gratis, primer mes al 50% del precio Growth. A cambio, feedback semanal y derecho a usar como caso de estudio anonimizable.
                </p>
              </div>

              <form onSubmit={onSubmit} className="mt-10 grid md:grid-cols-2 gap-4">
                <Field
                  label="Tu nombre"
                  required
                  value={form.name}
                  onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                />
                <Field
                  label="Email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                />
                <Field
                  label="Empresa"
                  value={form.company}
                  onChange={(v) => setForm((f) => ({ ...f, company: v }))}
                />
                <Field
                  label="Rubro"
                  placeholder="Ej: hotel, casino, distribuidora"
                  value={form.industry}
                  onChange={(v) => setForm((f) => ({ ...f, industry: v }))}
                />
                <Field
                  label="¿Cuántos grupos operativos activos?"
                  type="number"
                  value={form.groups_count}
                  onChange={(v) => setForm((f) => ({ ...f, groups_count: v }))}
                />
                <Field
                  label="¿Cuántos empleados los usan?"
                  type="number"
                  value={form.employees}
                  onChange={(v) => setForm((f) => ({ ...f, employees: v }))}
                />

                {errorMsg && (
                  <div className="md:col-span-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-700">
                    {errorMsg}
                  </div>
                )}

                <div className="md:col-span-2 mt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 px-5 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition disabled:opacity-50 font-medium"
                  >
                    {submitting ? 'Enviando…' : 'Quiero probar 30 días'} <ArrowRight size={16} />
                  </button>
                  <p className="mt-3 text-xs text-slate-500">
                    Te contactamos dentro de las 24h hábiles. Sin SDR, sin spam, sin secuencia automatizada.
                  </p>
                </div>
              </form>
            </>
          )}
        </div>
      </section>

      {/* === Footer === */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-slate-500">
            © Vipsoft · Producto en beta, validándose con pilotos LATAM.
          </div>
          <div className="text-xs text-slate-400">
            Operaciones internas únicamente · No es atención al cliente final
          </div>
        </div>
      </footer>
    </div>
  );
}

// ---------- Subcomponentes ----------

function Pain({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function Step({
  n,
  title,
  body,
  sample,
}: {
  n: number;
  title: string;
  body: string;
  sample: string;
}) {
  return (
    <div className="p-5 rounded-xl border border-slate-200 bg-white">
      <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 text-sm font-semibold">
        {n}
      </div>
      <h3 className="mt-4 font-semibold text-slate-900">{title}</h3>
      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
      <div className="mt-4 px-3 py-2 bg-emerald-50 rounded-lg text-xs text-emerald-900 font-mono leading-snug">
        {sample}
      </div>
    </div>
  );
}

function Feature({
  icon: Icon,
  title,
  body,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
}) {
  return (
    <div className="p-5">
      <Icon size={20} className="text-emerald-700" />
      <h3 className="mt-3 font-semibold text-slate-900">{title}</h3>
      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

const COMPARISON = [
  { label: 'Funciona en grupos de WhatsApp', ours: 'Sí', statusQuo: 'Sí, sin trazabilidad', enterprise: 'No', slack: 'No' },
  { label: 'El equipo no cambia de app', ours: 'Sí', statusQuo: 'Sí', enterprise: 'No', slack: 'No' },
  { label: 'Implementación', ours: 'Horas', statusQuo: 'N/A', enterprise: 'Semanas + consultor', slack: 'Días + entrenamiento' },
  { label: 'Self-hosted', ours: 'Opcional', statusQuo: 'N/A', enterprise: 'No', slack: 'No' },
  { label: 'Reporting operativo', ours: 'Sí, simple', statusQuo: 'Cero', enterprise: 'Sí, sobredimensionado', slack: 'Fragmentado entre apps' },
  { label: 'Soporte en español LATAM', ours: 'Sí', statusQuo: 'N/A', enterprise: 'Limitado', slack: 'Limitado' },
  { label: 'Integración a PMS / ERP local', ours: 'Sí, parte del setup', statusQuo: 'N/A', enterprise: 'Sí pero cara', slack: 'Fragmentada' },
];

function PricingCard({
  tier,
  price,
  cap,
  groups,
  agents,
  integrations,
  highlight,
}: {
  tier: string;
  price: string;
  cap: string;
  groups: string;
  agents: string;
  integrations: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`p-6 rounded-xl border ${
        highlight
          ? 'bg-emerald-500/10 border-emerald-500/30 ring-1 ring-emerald-400/40'
          : 'bg-slate-800/50 border-slate-700'
      }`}
    >
      {highlight && (
        <div className="inline-block px-2 py-0.5 text-xs font-medium bg-emerald-400/20 text-emerald-200 rounded-full mb-3">
          Más elegido
        </div>
      )}
      <h3 className="text-lg font-semibold">{tier}</h3>
      <div className="mt-2">
        <span className="text-3xl font-bold tracking-tight">{price}</span>
        <span className="text-slate-400 text-sm"> /mes</span>
      </div>
      <ul className="mt-5 space-y-2 text-sm text-slate-200">
        <li className="flex items-center gap-2">
          <CheckCircle2 size={14} className="text-emerald-400 shrink-0" /> {cap}
        </li>
        <li className="flex items-center gap-2">
          <CheckCircle2 size={14} className="text-emerald-400 shrink-0" /> {groups}
        </li>
        <li className="flex items-center gap-2">
          <CheckCircle2 size={14} className="text-emerald-400 shrink-0" /> {agents}
        </li>
        <li className="flex items-center gap-2">
          <CheckCircle2 size={14} className="text-emerald-400 shrink-0" /> {integrations}
        </li>
      </ul>
    </div>
  );
}

function Field({
  label,
  required,
  type = 'text',
  placeholder,
  value,
  onChange,
}: {
  label: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
      </span>
      <input
        type={type}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
      />
    </label>
  );
}
